import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Clock, Tag, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { ArticleReadingProgress } from "@/components/ArticleReadingProgress";
import { ArticleTOC, sectionId } from "@/components/ArticleTOC";
import { COMPANY } from "@/lib/company";
import { EN_INSIGHT_CATEGORY_LABEL } from "@/content/en-insights/types";
import type { EnInsightMeta } from "@/content/en-insights/meta.gen";
import { loadEnInsight } from "@/content/en-insights/lazy.gen";
import type { EnInsightPost } from "@/content/en-insights/types";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildFaqSchema,
} from "@/lib/schema";
import { INSIGHT_HERO_IMAGES, insightHeroAbsolute } from "@/content/insights/hero-images";
import { SmartImage } from "@/components/SmartImage";
import { EnHeader } from "../services/index";

export const Route = createFileRoute("/en/insights/$slug")({
  component: EnInsightDetail,
  loader: async ({ params }) => {
    const [post, { EN_INSIGHTS_META }] = await Promise.all([
      loadEnInsight(params.slug),
      import("@/content/en-insights/meta"),
    ]);
    if (!post) throw notFound();
    const { pickReciprocal } = await import("@/lib/nearby-links");
    const related = pickReciprocal(
      EN_INSIGHTS_META,
      EN_INSIGHTS_META.find((p) => p.slug === post.slug) ?? { ...post },
      (p) => p.category,
      4,
    );
    return { post, related };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background text-foreground">
      <EnHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">Article not found</h1>
        <Link
          to="/en/insights"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to insights
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-12 text-center">
      <h1 className="text-xl font-bold">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    const site = getActiveSiteUrl();
    if (!post) {
      return pageHeadForProvince({
        title: "Article not found — Thai Notary Law",
        description: "The article could not be found.",
        path: `/en/insights/${params.slug}`,
        locale: "en_US",
      });
    }
    const url = `${site}/en/insights/${post.slug}`;
    const bodyText = post.sections
      .map((s) => [s.h2, ...s.body, ...(s.bullets ?? [])].join(" "))
      .join(" ");
    const wordCount = bodyText.split(/\s+/).filter(Boolean).length;
    const head = pageHeadForProvince({
      title: post.metaTitle,
      description: post.metaDesc,
      path: `/en/insights/${post.slug}`,
      type: "article",
      image: post.heroImage ?? insightHeroAbsolute(post.category, site),
      locale: "en_US",
      alternates: post.thaiSlug
        ? [{ lang: "th", path: `/insights/${post.thaiSlug}` }]
        : [],
    });
    const scripts: { type: string; children: string }[] = [
      { type: "application/ld+json", children: JSON.stringify(buildOrganizationSchema()) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildBreadcrumbSchema([
            { name: "Home", url: `${site}/en` },
            { name: "Insights", url: `${site}/en/insights` },
            { name: post.title, url },
          ]),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.metaDesc,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt ?? post.publishedAt,
          author: { "@type": "Organization", name: COMPANY.brandName },
          publisher: {
            "@type": "Organization",
            name: COMPANY.brandName,
            logo: { "@type": "ImageObject", url: `${site}/favicon.webp` },
          },
          image: post.heroImage ?? insightHeroAbsolute(post.category, site),
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          articleSection: EN_INSIGHT_CATEGORY_LABEL[post.category],
          keywords: post.tags.join(", "),
          inLanguage: "en-US",
          about: { "@type": "Place", name: "Phuket, Thailand" },
          wordCount,
          timeRequired: `PT${post.readingMinutes}M`,
          articleBody: bodyText.slice(0, 500),
          speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: ["h1", ".tldr-speakable"],
          },
        }),
      },
    ];
    if (post.faqs?.length) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(buildFaqSchema(post.faqs)),
      });
    }
    return { ...head, scripts };
  },
});

function EnInsightDetail() {
  const { post, related } = Route.useLoaderData() as {
    post: EnInsightPost;
    related: EnInsightMeta[];
  };
  return (
    <div className="min-h-screen bg-background text-foreground" lang="en">
      <ArticleReadingProgress />
      <EnHeader />
      <main id="main-content">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 lg:grid-cols-[1fr_220px]">
          <article>
            <Link
              to="/en/insights"
              className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-3 w-3" /> All insights
            </Link>
            <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-gold">
              <Tag className="h-3 w-3" />
              {EN_INSIGHT_CATEGORY_LABEL[post.category]}
            </div>
            <h1 className="mt-2 text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">
              {post.title}
            </h1>
            <figure className="mt-6 overflow-hidden rounded-2xl border border-border">
              <SmartImage
                src={post.heroImage ?? INSIGHT_HERO_IMAGES[post.category].src}
                alt={INSIGHT_HERO_IMAGES[post.category].altEn}
                width={1200}
                height={630}
                priority
                className="h-auto w-full object-cover"
              />
            </figure>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>
                Published{" "}
                {new Date(post.publishedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
              {post.updatedAt && (
                <span>
                  Updated{" "}
                  {new Date(post.updatedAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> {post.readingMinutes} min read
              </span>
              {post.thaiSlug && (
                <Link
                  to="/insights/$slug"
                  params={{ slug: post.thaiSlug }}
                  className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider hover:bg-accent"
                >
                  ภาษาไทย <ArrowUpRight className="h-2.5 w-2.5" />
                </Link>
              )}
            </div>

            <div className="mt-8 rounded-xl border border-gold/30 bg-gold/5 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-gold">TL;DR</div>
              <p className="tldr-speakable mt-2 text-sm leading-relaxed text-foreground">
                {post.tldr}
              </p>
            </div>

            <div className="mt-10 space-y-10">
              {post.sections.map((s, idx) => (
                <section key={idx} id={sectionId(s, idx)} className="scroll-mt-24">
                  <h2 className="text-xl font-bold md:text-2xl">{s.h2}</h2>
                  {s.body.map((para, i) => (
                    <p key={i} className="mt-3 text-base leading-relaxed text-foreground/90">
                      {para}
                    </p>
                  ))}
                  {s.bullets && (
                    <ul className="mt-4 space-y-2">
                      {s.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}
            </div>

  
          {post.downloads && post.downloads.length > 0 && (
            <section className="mt-8 rounded-xl border border-primary/30 bg-primary/5 p-5">
              <h2 className="text-base font-bold">Downloads</h2>
              <ul className="mt-3 space-y-3">
                {post.downloads.map((d) => (
                  <li key={d.href}>
                    <a
                      href={d.href}
                      download
                      className="text-sm font-semibold text-primary underline underline-offset-4"
                    >
                      {d.label}
                    </a>
                    {d.note && (
                      <div className="mt-1 text-xs text-muted-foreground">{d.note}</div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {post.faqs && post.faqs.length > 0 && (
              <section className="mt-12 border-t border-border pt-10">
                <h2 className="text-xl font-bold md:text-2xl">Frequently asked questions</h2>
                <div className="mt-4 space-y-4">
                  {post.faqs.map((f, i) => (
                    <div key={i} className="rounded-lg border border-border bg-card p-4">
                      <div className="font-semibold text-foreground">{f.q}</div>
                      <div className="mt-1 text-sm text-muted-foreground">{f.a}</div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-12 rounded-xl bg-primary p-6 text-primary-foreground">
              <h2 className="text-lg font-bold">Got a case-specific question?</h2>
              <p className="mt-1 text-sm opacity-90">
                Free 15-minute consult with our English-speaking lawyers — LINE reply within 30
                minutes.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={`tel:${COMPANY.phoneIntl}`}
                  className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-bold text-primary"
                >
                  <Phone className="h-3 w-3" /> {COMPANY.phone}
                </a>
                <a
                  href="https://line.me/R/ti/p/@thainotary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-primary-foreground/40 px-4 py-2 text-sm font-bold"
                >
                  <MessageCircle className="h-3 w-3" /> LINE @THAINOTARY
                </a>
              </div>
            </section>
          </article>
          <aside>
            <ArticleTOC sections={post.sections} />
          </aside>
        </div>

        {related.length > 0 && (
          <section className="border-t border-border bg-muted/30">
            <div className="mx-auto max-w-5xl px-4 py-12">
              <h2 className="mb-6 text-xl font-bold">Related insights</h2>
              <div className="grid gap-4 md:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/en/insights/$slug"
                    params={{ slug: r.slug }}
                    className="group rounded-lg border border-border bg-card p-4 transition hover:border-primary"
                  >
                    <div className="text-xs font-semibold text-gold">
                      {EN_INSIGHT_CATEGORY_LABEL[r.category]}
                    </div>
                    <div className="mt-2 font-bold leading-snug group-hover:text-primary">
                      {r.title}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}